import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import {
  CheckCircle,
  Music,
  Disc,
  Users,
  User,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { SongCard } from '../../components/song/SongCard.js';
import { AlbumCard } from '../../components/song/AlbumCard.js';
import { artistsService } from '../../services/artists.service.js';
import { formatNumber } from '../../lib/format.js';

type Tab = 'songs' | 'albums' | 'about';

function ArtistSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-64 rounded-2xl bg-surface-hover mb-6" />
      <div className="h-6 w-48 bg-surface-hover rounded mb-2" />
      <div className="h-4 w-32 bg-surface-hover rounded mb-6" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-16 rounded-lg bg-surface-hover" />
        ))}
      </div>
    </div>
  );
}

const SOCIAL_ICONS: Record<string, { label: string; color: string }> = {
  spotify: { label: 'Spotify', color: 'hover:text-green-500' },
  youtube: { label: 'YouTube', color: 'hover:text-red-500' },
  instagram: { label: 'Instagram', color: 'hover:text-pink-500' },
  twitter: { label: 'Twitter', color: 'hover:text-sky-500' },
};

export default function ArtistPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<Tab>('songs');
  const [songsPage, setSongsPage] = useState(1);

  const { data: artistData, isLoading: artistLoading } = useQuery({
    queryKey: ['artist', slug],
    queryFn: () => artistsService.getArtistBySlug(slug!),
    enabled: !!slug,
  });

  const artist = artistData?.data;

  const { data: songsData, isLoading: songsLoading } = useQuery({
    queryKey: ['artist', slug, 'songs', songsPage],
    queryFn: () => artistsService.getArtistSongs(slug!, { page: songsPage, limit: 10 }),
    enabled: !!slug && activeTab === 'songs',
  });

  const { data: albumsData, isLoading: albumsLoading } = useQuery({
    queryKey: ['artist', slug, 'albums'],
    queryFn: () => artistsService.getArtistAlbums(slug!),
    enabled: !!slug && activeTab === 'albums',
  });

  const songs = songsData?.data ?? [];
  const albums = albumsData?.data ?? [];
  const songsMeta = (songsData as any)?.meta;
  const totalSongsPages = songsMeta?.totalPages ?? 1;

  if (artistLoading) {
    return <ArtistSkeleton />;
  }

  if (!artist) {
    return (
      <div className="text-center py-16">
        <User size={64} className="mx-auto mb-4 text-text-muted opacity-50" />
        <h1 className="text-2xl font-bold text-text-primary mb-2">Artista no encontrado</h1>
        <p className="text-text-secondary">El artista que buscas no existe.</p>
        <Link to="/" className="text-primary hover:text-primary/80 mt-4 inline-block">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const socialLinks = (artist as any).socialLinks ?? {};

  return (
    <>
      <Helmet>
        <title>{artist.name} - Letras y discografia | LyricsHub</title>
        <meta
          name="description"
          content={`Letras de canciones de ${artist.name}. Discografia completa y biografia en LyricsHub.`}
        />
        <meta property="og:title" content={`${artist.name} | LyricsHub`} />
        <meta property="og:description" content={`Letras y discografia de ${artist.name}`} />
        {artist.avatarUrl && <meta property="og:image" content={artist.avatarUrl} />}
      </Helmet>

      {/* Parallax Header */}
      <div className="relative rounded-2xl overflow-hidden mb-6 h-56 md:h-72">
        {(artist as any).coverUrl ? (
          <img
            src={(artist as any).coverUrl}
            alt={artist.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/30 via-primary/10 to-transparent" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end gap-4">
          {/* Avatar */}
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-surface-hover border-4 border-background shrink-0">
            {artist.avatarUrl ? (
              <img
                src={artist.avatarUrl}
                alt={artist.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-surface">
                <User size={40} className="text-text-muted" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0 text-white">
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2 truncate">
              {artist.name}
              {artist.isVerified && <CheckCircle size={22} className="text-primary shrink-0" />}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-1 text-sm text-white/70">
              <span className="flex items-center gap-1">
                <Music size={14} />
                {(artist as any).songCount ?? 0} canciones
              </span>
              <span className="flex items-center gap-1">
                <Disc size={14} />
                {(artist as any).albumCount ?? 0} albums
              </span>
              <span className="flex items-center gap-1">
                <Users size={14} />
                {formatNumber((artist as any).followerCount ?? 0)} seguidores
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Social Links */}
      {Object.keys(socialLinks).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(socialLinks).map(([platform, url]) => {
            const social = SOCIAL_ICONS[platform];
            if (!social || !url) return null;
            return (
              <a
                key={platform}
                href={url as string}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface border border-border text-sm text-text-secondary ${social.color} transition-colors`}
              >
                <ExternalLink size={14} />
                {social.label}
              </a>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-6">
        {[
          { key: 'songs' as Tab, label: 'Canciones', icon: Music },
          { key: 'albums' as Tab, label: 'Albums', icon: Disc },
          { key: 'about' as Tab, label: 'Sobre el artista', icon: User },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'songs' && (
        <div>
          {songsLoading ? (
            <div className="space-y-2 animate-pulse">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-2">
                  <div className="w-14 h-14 rounded-lg bg-surface-hover" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-surface-hover rounded w-1/2" />
                    <div className="h-3 bg-surface-hover rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : songs.length === 0 ? (
            <p className="text-center text-text-muted py-12">No hay canciones disponibles.</p>
          ) : (
            <>
              <div className="space-y-1">
                {songs.map((song: any, i: number) => (
                  <SongCard
                    key={song.id}
                    slug={song.slug}
                    title={song.title}
                    artistName={song.artistName}
                    artistSlug={song.artistSlug}
                    coverUrl={song.coverUrl}
                    genreName={song.genreName}
                    viewCount={song.viewCount}
                    variant="horizontal"
                    index={(songsPage - 1) * 10 + i}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalSongsPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setSongsPage((p) => Math.max(1, p - 1))}
                    disabled={songsPage <= 1}
                    className="p-2 rounded-lg hover:bg-surface-hover text-text-muted disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="text-sm text-text-secondary">
                    Pagina {songsPage} de {totalSongsPages}
                  </span>
                  <button
                    onClick={() => setSongsPage((p) => Math.min(totalSongsPages, p + 1))}
                    disabled={songsPage >= totalSongsPages}
                    className="p-2 rounded-lg hover:bg-surface-hover text-text-muted disabled:opacity-40 transition-colors"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'albums' && (
        <div>
          {albumsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 animate-pulse">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-xl overflow-hidden bg-surface">
                  <div className="aspect-square bg-surface-hover" />
                  <div className="p-3 space-y-2">
                    <div className="h-4 bg-surface-hover rounded w-3/4" />
                    <div className="h-3 bg-surface-hover rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : albums.length === 0 ? (
            <p className="text-center text-text-muted py-12">No hay albums disponibles.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {albums.map((album: any) => (
                <AlbumCard
                  key={album.id}
                  slug={album.slug}
                  title={album.title}
                  artistName={artist.name}
                  artistSlug={artist.slug}
                  coverUrl={album.coverUrl}
                  releaseYear={album.releaseYear}
                  songCount={(album as any).songCount}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'about' && (
        <div className="max-w-2xl">
          {(artist as any).bio ? (
            <div className="prose prose-sm text-text-secondary leading-relaxed whitespace-pre-line">
              {(artist as any).bio}
            </div>
          ) : (
            <p className="text-text-muted py-12 text-center">
              No hay biografia disponible para este artista.
            </p>
          )}
          {(artist as any).country && (
            <div className="mt-6 text-sm text-text-secondary">
              <span className="font-medium text-text-primary">Pais:</span> {(artist as any).country}
            </div>
          )}
          {(artist as any).activeSince && (
            <div className="mt-2 text-sm text-text-secondary">
              <span className="font-medium text-text-primary">Activo desde:</span>{' '}
              {(artist as any).activeSince}
            </div>
          )}
        </div>
      )}
    </>
  );
}
